import { dispatcher } from 'lib/Dispatcher';
import { ActionTypes } from 'groups/constants/GroupConstants';
import Message from 'entities/Message';
import Messages from 'collections/Messages';
import ToastActions from 'actions/ToastActions';
import { getGroupKey } from 'utils/roleHelpers';
import errorLogger from 'utils/errorLogger';
import { saveReactionSummary } from 'chat/components/Reaction';
import type { V2File } from 'clients/api/fileUploads';

import type { Target, GroupTargetRole } from 'entities/Message';

const MESSAGES_PAGE_SIZE: number = 10;

function writeApolloCacheForMessages(messages: any) {
  try {
    // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'message' implicitly has an 'any' type.
    messages.forEach((message) => {
      if (message.getReactionSummary) {
        const reaction = message.getReactionSummary().toGraphQL();

        if (reaction.messageUuid) {
          saveReactionSummary(reaction);
        }
      }
    });
  } catch (e) {
    console.error('could not write message into cache:', e);
  }
}

const MessageActions = {
  // XXX (rgoldfinger) remove when cleaning up old way of sending messages
  loadMessages(
    groupId: Backbone$Id,
    maxSeq: number | undefined | null = null,
    sinceSeq: number = 0,
    limit: number = MESSAGES_PAGE_SIZE
  ) {
    const options = {
      max_seq: maxSeq,
      since_seq: sinceSeq,
      limit,
    };

    return Messages.yieldMessagesForGroupId(groupId, options).then((messages) => {
      dispatcher.dispatch({
        type: ActionTypes.LIST_GROUP_MESSAGES,
        groupKey: groupId,
        messages,
      });
    });
  },

  loadThreadMessages(
    groupId: Backbone$Id,
    threadUuid: string,
    role: GroupTargetRole,
    opts: any = {}
  ): void {
    const options = {
      max_seq: null,
      since_seq: 0,
      limit: MESSAGES_PAGE_SIZE,
      ...opts,
    };

    Messages.yieldThreadMessagesForGroupId(groupId, threadUuid, options)
      .then((messages) => {
        dispatcher.dispatch({
          type: ActionTypes.LIST_GROUP_MESSAGES,
          groupKey: getGroupKey(groupId, role),
          messages,
        });
        writeApolloCacheForMessages(messages);
      })
      .catch(errorLogger('loadThreadMessages failure'));
  },

  sendMessage(message: Message): Promise<void> {
    const isScheduled = !!message.getSendAt();
    const type = isScheduled
      ? {
          success: ActionTypes.SEND_GROUP_SCHEDULED_MESSAGE,
          error: ActionTypes.SEND_GROUP_SCHEDULED_MESSAGE_ERROR,
        }
      : {
          success: ActionTypes.SEND_GROUP_MESSAGE,
          error: ActionTypes.SEND_GROUP_MESSAGE_ERROR,
        };

    return message
      .send()
      .then((messageSent: any) => {
        if (isScheduled) {
          ToastActions.showSuccessToast('Announcement scheduled');
        } else {
          const groupsCount = (messageSent as Message).getTargets().length;
          ToastActions.showSuccessToast(
            groupsCount <= 1 ? 'Announcement sent' : `${groupsCount} Announcements sent`
          );
        }

        dispatcher.dispatch({
          type: type.success,
          message: messageSent,
        });
      })
      .catch((error) => {
        console.warn(error, error && error.stack);
        if (error.code !== 'duplicate_message') {
          console.error(`MessageActions.sendMessage ${error.code} error`);
        }
        ToastActions.showAPIError(error);
        dispatcher.dispatch({
          type: type.error,
          message,
          error,
        });
      });
  },

  export(groupIds: number[], startDate: Date, endDate: Date): void {
    Messages.export(groupIds, startDate, endDate)
      .then(() => {
        ToastActions.showSuccessToast(
          'We’ve emailed you the Announcement history you’ve requested. You should receive it within 10 minutes.'
        );
      })
      .catch((error) => {
        ToastActions.showAPIError(error);
        throw error;
      });
  },
  updateScheduledMessage(
    messageToUpdate: Message,
    body: string,
    targets: Target[],
    files: V2File[],
    sendAt: Date
  ): Promise<any> {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
    const changes = messageToUpdate.update(body, targets, sendAt) || {};
    return messageToUpdate
      .patch(changes, files)
      .then((message) => {
        ToastActions.showSuccessToast('Announcement updated');
        dispatcher.dispatch({
          type: ActionTypes.UPDATED_GROUP_SCHEDULED_MESSAGE,
          message,
        });
      })
      .catch((error) => {
        ToastActions.showAPIError(error);
        throw error;
      });
  },
  destroyScheduledMessage(messageToDestroy: Message): void {
    messageToDestroy
      .destroy()
      .then((message) => {
        ToastActions.showSuccessToast('Announcement deleted');
        dispatcher.dispatch({
          type: ActionTypes.DESTROY_GROUP_SCHEDULED_MESSAGE,
          message,
        });
      })
      .catch((error) => {
        ToastActions.showAPIError(error);
        throw error;
      });
  },
};
export default MessageActions;
